import {getLogger} from '../logger.ts';
import {Authenticator, gatewayToken, isGatewayClientAuthentication} from './core.ts';
import type {
    AuthenticationRequest,
    AuthenticationResponse
} from './types.ts';
import { convertToken } from '../common/tokens.ts';
import AsyncSerializer from '../common/asyncSerializer.ts';
import type { GatewayRequest } from '../state/types/pending-requests.ts';
import { sanitizerOf } from '../common/utilities.ts';

export interface AuthImpl {
    auth(authenticationRequest: AuthenticationRequest): Promise<AuthenticationResponse>
}

async function handleGwTokenAuthentication(signatureKey: string, token: string): Promise<AuthenticationResponse> {
    const gwToken = convertToken(signatureKey, token);
    switch (gwToken.type) {
        case 'gw-request': {
            const gwRequest = gwToken['gw-request'] as GatewayRequest | undefined;
            if (gwRequest) {
                const impersonatePeer = gwToken['impersonate-peer'] as {user: string} | undefined;
                return {type: 'success', gwRequest, impersonatePeer};
            }
            throw new Error('Token is missing the gateway request information');
        }
        case 'authentication': {
            const user = gwToken['user'] as string | undefined;
            if (user) {
                return {type: 'success', user};
            }
            throw new Error('Token is missing the impersonation information');
        }
        default: {
            throw new Error(`Invalid gateway token type: ${gwToken.type}`);
        }
    }
}

async function handleGwClientAuthentication({clientAuth, authentication, remoteIdentity, requestId}: AuthenticationRequest): Promise<AuthenticationResponse> {
    if (clientAuth === undefined) {
        throw new Error("'gateway-client' authentication method requires a client authentication callback to be provided");
    }
    return await clientAuth({requestId, authentication, remoteIdentity});
}

const logger = getLogger("gateway.auth.impl")

class DefaultAuthenticator implements Authenticator {
    readonly #timeout: number;
    readonly #ch: AsyncSerializer;
    readonly #processor: (resolve: (response: PromiseLike<AuthenticationResponse> | AuthenticationResponse) => void, reject: (err?: unknown) => void, authenticationRequest: AuthenticationRequest) => void;

    constructor(timeout: number,
                maxPendingRequests: number,
                processor: (resolve: (response: PromiseLike<AuthenticationResponse> | AuthenticationResponse) => void, reject: (err?: unknown) => void, authenticationRequest: AuthenticationRequest) => void) {
        this.#timeout = timeout;
        this.#ch = new AsyncSerializer(maxPendingRequests);
        this.#processor = processor;
    }

    stop() {
        this.#ch.close();
    }

    async authenticate(authenticationRequest: AuthenticationRequest): Promise<AuthenticationResponse> {
        const token = gatewayToken(authenticationRequest.authentication)
        if (token) {
            return await handleGwTokenAuthentication(authenticationRequest.signatureKey, token);
        } else if (isGatewayClientAuthentication(authenticationRequest.authentication)) {
            return await handleGwClientAuthentication(authenticationRequest);
        } else {
            let timeoutId: NodeJS.Timeout;
            return await Promise.race<AuthenticationResponse>([
                this.#ch.put<AuthenticationResponse>(() => {
                    return new Promise<AuthenticationResponse>((resolve, reject) => {
                        const {authentication} = authenticationRequest;
                        if (logger.enabledFor('debug')) {
                            logger.debug(`processing authentication ${JSON.stringify(authentication, sanitizerOf('secret', 'token', 'providerContext'))}`)
                        }
                        this.#processor(resolve, reject, authenticationRequest)
                    })
                }),
                new Promise<AuthenticationResponse>((resolve, reject) => {
                    timeoutId = setTimeout(() => {
                        reject(new Error("timeout"));
                    }, this.#timeout);
                })
            ]).finally(() => clearTimeout(timeoutId));
        }
    }
}

const defaultConfig = {timeout: 5000, max_pending_requests: 20000}

export interface DefaultAuthenticatorConfig {
    timeout?: number,
    max_pending_requests?: number
}

export function authenticator(config: DefaultAuthenticatorConfig, impl: AuthImpl): Authenticator {
    return new DefaultAuthenticator(
        config.timeout ?? defaultConfig.timeout,
        config.max_pending_requests ?? defaultConfig.max_pending_requests,
        (resolve, reject, authenticationRequest: AuthenticationRequest) => {
            impl.auth(authenticationRequest)
                .then((v) => resolve(v))
                .catch((err) => reject(err));
        });
}
